// import {useEffect} from 'react'
import {Routes, Route, Navigate} from 'react-router-dom'
import {Spin} from 'antd'

import styles from '../App.module.scss'
import useStore from 'base/store'
import {Profile, useGetProfile} from 'modules/profile'
import {Cashflow} from 'modules/cashflow'
import {
  Requests,
  LoanRequests,
  DrawdownRequests,
  VariationRequests,
  DefaultRequests,
  JournalRequests,
} from 'modules/requests'
import {
  LoanRequest, VariationRequest, DefaultRequest, JournalRequest,
  RequestAbout, RequestAboutJournal, RequestAboutDefault, RequestAboutVariation,
} from 'modules/request'
import {
  Projects,
  CurrentProjects,
  ClosedProjects,
  UserProjects,
  UserCurrentProjects,
  UserArchivedProjects,
} from 'modules/projects'
import {
  Project,
  BrokerProject,
  ProjectAbout,
  ProjectDrawdowns,
  ProjectSchedule,
  ProjectForecast,
  BorrowerProject,
  BorrowerProjectAbout,
  BorrowerProjectDrawdowns,
  BorrowerProjectSchedule,
  BorrowerProjectConditions,
} from 'modules/project'
import {
  Documents,
  DocumentsAml,
  DocumentsProposal,
  DocumentsLegal,
  DocumentsDrawdowns,
  DocumentsProjectFiles,
  DocumentsChat,
  DocumentsOthers,
  DocumentsSearch,
} from 'modules/documents'
import {Users, AllUsersList, /*Borrowers, Team, Brokers*/} from 'modules/users'
import {
  OrgDetails, 
  Organisations, 
  OrgsListOutletBorrowers, OrgsListOutletTeam, OrgsListOutletBrokers
} from 'modules/organisations'
import {
  Reconciliations, 
  ReconciliationOutlet, StatementTableOutlet, TransactionsTableOutlet,
} from 'modules/reconciliations'
import {
  RBACPage, 
  RolesList, RoleDetails,
  UsersList as RBACUsersList, UserDetails as RBACUserDetails,
} from 'modules/rbac'
import {Test} from 'modules/test'
import {Box} from 'common/components'
import { PermissionType } from 'common/types'
// import {useNoReloadLogout} from 'common/hooks'
// import {showErrorNotification} from 'common/helpers'
import {FetchLoader} from '.'

export default function AppContent() {
  const role = useStore((state) => state.role)
  const setRole = useStore((state) => state.setRole)
  const setPerm = useStore((state) => state.setPermissions)
  const setUser = useStore((state) => state.setUserInfo)
  // const logout = useNoReloadLogout()

  useGetProfile({
    onSuccess(data) {
      setUser(data);
      setRole(data?.role)
      setPerm(data?.permissions?.map(
        (x:string) => (x.toLocaleLowerCase().trim() as PermissionType)
      ))
    },
  })

  // useEffect(() => {
  //   if (role && role.root) {
  //     logout()
  //     showErrorNotification('Access denied.')
  //   }
  // }, [role]) // eslint-disable-line

  if (!role)
    return (
      <Box fadeIn className={styles.spinWrapper}>
        <Spin size="large" />
      </Box>
    )

  return (
    <>
      <FetchLoader />
      <Box fadeIn as="main">
        {role.user ? (
          <Routes>
            <Route path="projects" element={<UserProjects />}>
              <Route index element={<UserCurrentProjects />} />
              <Route path="archived" element={<UserArchivedProjects />} />
            </Route>
            <Route path="projects/:uuid" element={<BorrowerProject />}>
              <Route index element={<BorrowerProjectAbout />} />
              <Route path="drawdowns" element={<BorrowerProjectDrawdowns />} />
              <Route path="schedule" element={<BorrowerProjectSchedule />} />
              <Route
                path="conditions"
                element={<BorrowerProjectConditions />}
              />

              <Route path="documents" element={<Documents />}>
                <Route index element={<Navigate replace to="project" /> } />
                {/* <Route index element={<DocumentsAml />} /> */}
                {/* <Route path="proposal" element={<DocumentsProposal />} /> */}
                <Route path="legal" element={<DocumentsLegal />} />
                <Route path="drawdowns" element={<DocumentsDrawdowns />} />
                <Route path="project" element={<DocumentsProjectFiles />} />
                {/* <Route path="others" element={<DocumentsOthers />} /> */}
                {/* <Route path="attachment" element={<DocumentsChat />} /> */}
                {/* <Route path="search" element={<DocumentsSearch />} /> */}
              </Route>

            </Route>
            <Route path="*" element={<Navigate replace to="projects" />} />
          </Routes>
        ) : role.broker ? (
          <Routes>
            <Route path="projects" element={<UserProjects />}>
              <Route index element={<UserCurrentProjects />} />
              <Route path="archived" element={<UserArchivedProjects />} />
            </Route>
            <Route path="projects/:uuid" element={<BrokerProject />} />
            <Route path="*" element={<Navigate replace to="projects" />} />
          </Routes>
        ) : (
          <Routes>
            <Route path="projects" element={<Projects />}>
              <Route index element={<CurrentProjects />} />
              <Route path="closed" element={<ClosedProjects />} />
            </Route>
            <Route path="projects/:uuid" element={<Project />}>
              <Route index element={<ProjectAbout />} />
              <Route path="drawdowns" element={<ProjectDrawdowns />} />
              <Route path="schedule" element={<ProjectSchedule />} />
              <Route path="forecast" element={<ProjectForecast />} />
              <Route path="documents" element={<Documents />}>
                <Route index element={<DocumentsAml />} />
                <Route path="proposal" element={<DocumentsProposal />} />
                <Route path="legal" element={<DocumentsLegal />} />
                <Route path="drawdowns" element={<DocumentsDrawdowns />} />
                <Route path="project" element={<DocumentsProjectFiles />} />
                <Route path="others" element={<DocumentsOthers />} />
                <Route path="attachment" element={<DocumentsChat />} />
                <Route path="search" element={<DocumentsSearch />} />
              </Route>
            </Route>
            <Route path="requests" element={<Requests />}>
              <Route index element={<LoanRequests />} />
              <Route path="drawdown"  element={<DrawdownRequests />} />
              <Route path="variation" element={<VariationRequests />} />
              <Route path="default"   element={<DefaultRequests />} />
              <Route path="journal"   element={<JournalRequests />} />
            </Route>
            <Route path="requests/loan/:uuid" element={<LoanRequest />}>
              <Route index element={<RequestAbout />} />
              <Route path="schedule" element={<ProjectSchedule isRequest />} />
              <Route path="documents" element={<Documents isRequest />}>
                <Route index element={<DocumentsAml isRequest />} />
                <Route
                  path="proposal"
                  element={<DocumentsProposal isRequest />}
                />
                <Route path="legal" element={<DocumentsLegal isRequest />} />
                <Route
                  path="project"
                  element={<DocumentsProjectFiles isRequest />}
                />
                <Route path="others" element={<DocumentsOthers isRequest />} />
                <Route
                  path="attachment"
                  element={<DocumentsChat isRequest />}
                />
                <Route path="search" element={<DocumentsSearch isRequest />} />
              </Route>
            </Route>
            <Route path="requests/variation/:uuid" element={<VariationRequest />}>
              <Route index element={<RequestAboutVariation />} />
              <Route path="schedule" element={<ProjectSchedule isRequest tag="variation" />} />
              <Route path="documents" element={<Documents isRequest tag="variation" />}>
                <Route index element={<DocumentsAml isRequest tag="variation" />} />
                <Route
                  path="proposal"
                  element={<DocumentsProposal isRequest tag="variation" />}
                />
                <Route path="legal" element={<DocumentsLegal isRequest tag="variation" />} />
                <Route
                  path="project"
                  element={<DocumentsProjectFiles isRequest tag="variation" />}
                />
                <Route path="others" element={<DocumentsOthers isRequest tag="variation" />} />
                <Route
                  path="attachment"
                  element={<DocumentsChat isRequest tag="variation" />}
                />
                <Route path="search" element={<DocumentsSearch isRequest tag="variation" />} />
              </Route>
            </Route>
            {/* <Route path="requests/drawdown/:uuid" element={<DrawdownRequest />}>
              <Route index element={<RequestAbout />} />
            </Route> */}
            <Route path="requests/default/:uuid" element={<DefaultRequest />}>
              <Route index element={<RequestAboutDefault />} />
              <Route path="documents" element={<Documents isRequest tag="default" />}>
                <Route index element={<DocumentsAml isRequest tag="default" />} />
                <Route
                  path="proposal"
                  element={<DocumentsProposal isRequest tag="default" />}
                />
                <Route path="legal" element={<DocumentsLegal isRequest tag="default" />} />
                <Route
                  path="project"
                  element={<DocumentsProjectFiles isRequest tag="default" />}
                />
                <Route path="others" element={<DocumentsOthers isRequest tag="default" />} />
                <Route
                  path="attachment"
                  element={<DocumentsChat isRequest tag="default" />}
                />
                <Route path="search" element={<DocumentsSearch isRequest tag="default" />} />
              </Route>
            </Route>
            <Route path="requests/journal/:uuid" element={<JournalRequest />}>
              <Route index element={<RequestAboutJournal />} />
              <Route path="documents" element={<Documents isRequest tag="journal" />}>
                <Route index element={<DocumentsAml isRequest tag="journal" />} />
                <Route
                  path="proposal"
                  element={<DocumentsProposal isRequest tag="journal" />}
                />
                <Route path="legal" element={<DocumentsLegal isRequest tag="journal" />} />
                <Route
                  path="project"
                  element={<DocumentsProjectFiles isRequest tag="journal" />}
                />
                <Route path="others" element={<DocumentsOthers isRequest tag="journal" />} />
                <Route
                  path="attachment"
                  element={<DocumentsChat isRequest tag="journal" />}
                />
                <Route path="search" element={<DocumentsSearch isRequest tag="journal" />} />
              </Route>
            </Route>
            {!role['associated-manager'] && (
              <Route path="cashflow" element={<Cashflow />} />
            )}
            <Route path="users" element={<Users />}>
              <Route index element={<AllUsersList />} />
              {/* <Route index element={<Borrowers />} />
                {!role.manager && (
                <>
                  <Route path="team" element={<Team />} />
                  <Route path="brokers" element={<Brokers />} />
                </>
              )} */}
            </Route>
            <Route path="orgs" element={<Organisations />}>
              <Route index          element={<OrgsListOutletBorrowers />} />
              <Route path="team"    element={<OrgsListOutletTeam />} />
              <Route path="brokers" element={<OrgsListOutletBrokers />} />
            </Route>
            <Route path="reconciliations" element={<Reconciliations />}>
              <Route index                element={<ReconciliationOutlet />} />
              <Route path="statements"    element={<StatementTableOutlet />} />
              <Route path="transactions"  element={<TransactionsTableOutlet />} />
            </Route>
            <Route path="org/details/:uuid" element={<OrgDetails />}>
            </Route>
            <Route path="rbac" element={<RBACPage />}>
              <Route index element={<RolesList />} />
              <Route path="role/:uuid" element={<RoleDetails />} />
              <Route path="users" element={<RBACUsersList />} />
              <Route path="user/:uuid" element={<RBACUserDetails />} />
            </Route>
            <Route path="profile" element={<Profile />} />
            <Route path="test" element={<Test />} />
            <Route path="*" element={<Navigate replace to="projects" />} />
          </Routes>
        )}
      </Box>
    </>
  )
}
